import React, { useState } from "react";
import PropTypes from "prop-types";
import { Calendar } from "react-modern-calendar-datepicker";
import { cn, customLocale } from "../util";
import { CalendarIcon } from "@heroicons/react/24/solid";

import { Popover, Transition } from "@headlessui/react";
import { Fragment } from "react";
import PrimaryButton from "./PrimaryButton";
import useFormatDate from "../hooks/useFormatDate";
import { utils } from "./DatePicker";

function getNestedError(errors, name) {
  return name.split(".").reduce((acc, part) => acc?.[part], errors);
}

export function DateTimeInput({
  name,
  label,
  timeLabel = "Hora",
  placeholder = "dd/mm/yyyy, hh:mm:ss",
  position = "top",
  minimumDate = null,
  maximumDate = null,
  className = "",
  register,
  rules = {},
  setValue,
  value,
  errors,
  disabled = false,
  ...rest
}) {
  const { formatDate } = useFormatDate();
  const { toDate, toNativeDate } = utils();

  const [selectedDateTime, setSelectedDateTime] = useState(value);
  const [selectedDate, setSelectedDate] = useState(
    toDate(value ? new Date(value) : new Date())
  );
  const [selectedTime, setSelectedTime] = useState(
    value ? formatDate(new Date(value), "HH:mm:ss") : "00:00:00"
  );

  const applyDateTime = () => {
    const date = formatDate(toNativeDate(selectedDate), "YYYY-MM-DD");
    const datetime = new Date(`${date}T${selectedTime}`).getTime();
    setSelectedDateTime(datetime);
    setValue(name, datetime);
  };
  const error = getNestedError(errors, name);
  const hasError = !!error;

  return (
    <Popover className="relative w-full">
      {({ close }) => (
        <>
          <label
            htmlFor={name}
            className="block text-sm font-medium text-gray-500"
          >
            {label}
          </label>
          <Popover.Button
            disabled={disabled}
            as="div"
            className="w-full relative disabled:cursor-not-allowed"
          >
            <input
              id={name}
              placeholder="dd/mm/yyyy, hh:mm:ss"
              disabled={disabled}
              value={
                selectedDateTime
                  ? formatDate(selectedDateTime, "DD/MM/YYYY, hh:mm:ss A")
                  : ""
              }
              className={cn(
                "font-normal border-v2-gray-border-tables text-v2-text-bar-steps focus:ring-v2-blue-text-login focus:border-v2-blue-text-login transition-all block w-full px-4 my-1 rounded-md cursor-pointer disabled:cursor-not-allowed",
                hasError && "border-red-500",
                className
              )}
              readOnly
              {...rest}
            />
            {/* REAL hidden field for RHF */}
            <input type="hidden" {...register(name, rules)} />
            <div className="absolute inset-y-0 max-w-[1.25rem] right-0 mr-3 flex items-center overflow-hidden">
              <CalendarIcon
                className={cn(
                  "w-5 h-5",
                  hasError ? "text-red-500" : "text-v2-blue-text-login",
                  disabled ? "opacity-50" : ""
                )}
              />
            </div>
          </Popover.Button>
          <p
            role="alert"
            className={cn(
              "mt-1 text-xs text-red-500",
              hasError ? "visible" : "invisible"
            )}
          >
            {error?.message || "Error"}
          </p>

          {/* Popover Content */}
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel
              className="
          absolute left-1/2 transform -translate-x-1/2 -mt-3 w-fit 
          rounded-xl border border-gray-200 bg-white shadow-lg
          z-10
          "
            >
              <div className="flex flex-col gap-4">
                <div className="datetime-input">
                  <Calendar
                    value={selectedDate}
                    onChange={setSelectedDate}
                    locale={customLocale}
                    wrapperClassName="z-10"
                    calendarClassName="block font-normal text-v2-input-text rounded-b-none"
                    calendarPopperPosition={position}
                    colorPrimary="#2169AC"
                    minimumDate={minimumDate ? toDate(minimumDate) : null}
                    maximumDate={maximumDate ? toDate(maximumDate) : null}
                  />
                </div>
                <div className="px-2">
                  <label
                    htmlFor="time"
                    className="block text-sm font-medium text-gray-500"
                  >
                    {timeLabel}
                  </label>
                  <div className="relative">
                    <input
                      id="time"
                      type="time"
                      lang="es-Mx"
                      step="1"
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                      placeholder={placeholder}
                      className={cn(
                        "font-normal border-v2-gray-border-tables text-v2-text-bar-steps focus:ring-v2-blue-text-login focus:border-v2-blue-text-login transition-all block w-full px-2 text-sm my-1 rounded-md"
                      )}
                    />
                  </div>
                </div>
                <div className="px-2 pb-2 w-full">
                  <PrimaryButton
                    type="button"
                    isFullWidth={true}
                    onClick={() => {
                      applyDateTime();
                      close();
                    }}
                    disabled={!selectedDate || !selectedTime}
                  >
                    Aplicar
                  </PrimaryButton>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

DateTimeInput.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  timeLabel: PropTypes.string,
  className: PropTypes.string,
  errors: PropTypes.object,
  value: PropTypes.any,
  setValue: PropTypes.func.isRequired,
  position: PropTypes.string,
  minimumDate: PropTypes.any,
  maximumDate: PropTypes.any,
  placeholder: PropTypes.string,
  register: PropTypes.func.isRequired,
  rules: PropTypes.object,
  disabled: PropTypes.bool,
};
